import { Feature, FeatureCollection, Polygon, MultiPolygon } from "geojson";
import { MapaDeChuvaFiltroIndice } from "../typings/mapaDeChuva";
import {
  DEFAULT_SURFACE_WIDTH,
  RAINFALL_UNIT,
} from "./constants";
import { extractRainfallSamples } from "./extractStations";
import { dedupeRainfallStations } from "./dedupeStations";
import { extractTalhaoClipPolygons, expandBoundsWithClipPolygons } from "./clipMask";
import { resolveOverlayBounds } from "./idw";
import { resolveRainfallBands } from "./bands";
import { renderRainfallSurfaceAsync } from "./renderSurface";
import { GeoBounds, RainfallBand, RainfallStation } from "./types";

export interface RainfallMapExportPoints extends FeatureCollection {
  Properties?: {
    latitudeMinima?: number;
    latitudeMaxima?: number;
    longitudeMinima?: number;
    longitudeMaxima?: number;
  };
}

export interface ExportRainfallMapPngOptions {
  points: RainfallMapExportPoints;
  indices?: MapaDeChuvaFiltroIndice[] | null;
  selectedBandCodes: number[];
  /** Farm/unidade name shown in the centered header title. */
  fazendaNome?: string | null;
  /** Selected rainfall period shown in the footer. */
  periodo?: RainfallExportPeriod | null;
  /** Full image width in px (default 1600). */
  width?: number;
  filename?: string;
}

export interface RainfallExportPeriod {
  dataInicial?: string | null;
  dataFinal?: string | null;
  horaInicial?: string | null;
  horaFinal?: string | null;
}

export interface RainfallExportAverages {
  mediaFazendaMm: number;
  mediaChuvaMm: number;
}

interface PixelPoint {
  x: number;
  y: number;
}

interface MapLayout {
  canvasWidth: number;
  canvasHeight: number;
  mapX: number;
  mapY: number;
  mapWidth: number;
  mapHeight: number;
  bounds: GeoBounds;
}

const PAD_TOP = 72;
const PAD_RIGHT = 56;
const FOOTER_HEIGHT = 84;
const SCALE_GAP = 40;
const PAD_BOTTOM = FOOTER_HEIGHT + SCALE_GAP;
const PAD_LEFT = 120;
const HEADER_CENTER_Y = 34;
const HEADER_TITLE_SUFFIX = "Mapa de Índices Pluviométricos";
const AMAGGI_LOGO_URL = `${process.env.PUBLIC_URL || ""}/logo-amaggi-oficial.png`;
const FOOTER_LOGO_MAX_WIDTH = 160;
const FOOTER_LOGO_MAX_HEIGHT = 52;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function project(
  lat: number,
  lng: number,
  layout: MapLayout
): PixelPoint {
  const { bounds, mapX, mapY, mapWidth, mapHeight } = layout;
  const x =
    mapX +
    ((lng - bounds.west) / Math.max(bounds.east - bounds.west, 1e-9)) *
      mapWidth;
  const y =
    mapY +
    ((bounds.north - lat) / Math.max(bounds.north - bounds.south, 1e-9)) *
      mapHeight;
  return { x, y };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

async function loadImageOptional(url: string): Promise<HTMLImageElement | null> {
  try {
    return await loadImage(url);
  } catch {
    return null;
  }
}

function formatMmLabel(value: number): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1).replace(/\.0$/, "");
}

function formatStationLabel(mm: number): string {
  return `${formatMmLabel(mm)} ${RAINFALL_UNIT}`;
}

function bandTickLabel(band: RainfallBand): string {
  if (band.minMm === Number.NEGATIVE_INFINITY && Number.isFinite(band.maxMm)) {
    return `≤${formatMmLabel(band.maxMm)}`;
  }
  if (band.maxMm === Number.POSITIVE_INFINITY && Number.isFinite(band.minMm)) {
    return `>${formatMmLabel(band.minMm)}`;
  }
  if (Number.isFinite(band.minMm) && Number.isFinite(band.maxMm)) {
    return `${formatMmLabel(band.minMm)}-${formatMmLabel(band.maxMm)}`;
  }
  return band.label.replace(/\s*mm\s*/gi, "").trim() || String(band.codigo);
}

export function formatRainfallMapHeaderTitle(
  fazendaNome?: string | null
): string {
  const nome = fazendaNome?.trim();
  if (!nome) {
    return HEADER_TITLE_SUFFIX;
  }
  const withPrefix = /^fazenda\b/i.test(nome) ? nome : `Fazenda ${nome}`;
  return `${withPrefix} - ${HEADER_TITLE_SUFFIX}`;
}

function pad2(value: number | string): string {
  return String(value).padStart(2, "0");
}

function formatExportDate(value?: string | null): string {
  if (!value) {
    return "";
  }
  const trimmed = value.trim();
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split("/");
    return `${pad2(day)}/${pad2(month)}/${year}`;
  }

  const datePart = trimmed.split(/[T\s]/)[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) {
    return trimmed;
  }
  return `${pad2(day)}/${pad2(month)}/${year}`;
}

function formatExportHour(value?: string | null): string {
  if (value == null || String(value).trim() === "") {
    return "00:00";
  }
  const trimmed = String(value).trim();
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [hours, minutes] = trimmed.split(":");
    return `${pad2(hours)}:${minutes}`;
  }
  if (/^\d{1,2}$/.test(trimmed)) {
    return `${pad2(trimmed)}:00`;
  }
  return trimmed;
}

export function formatRainfallExportPeriod(
  periodo?: RainfallExportPeriod | null
): string {
  if (!periodo) {
    return "—";
  }
  const startDate = formatExportDate(periodo.dataInicial);
  const endDate = formatExportDate(periodo.dataFinal);
  if (!startDate && !endDate) {
    return "—";
  }
  const startHour = formatExportHour(periodo.horaInicial);
  const endHour = formatExportHour(periodo.horaFinal);
  const start = startDate || endDate;
  const end = endDate || startDate;
  return `${start} - ${startHour} até ${end} - ${endHour}`;
}

export function computeRainfallExportAverages(
  stations: RainfallStation[]
): RainfallExportAverages {
  const total = stations.length;
  const sum = stations.reduce(
    (acc, station) => acc + (Number.isFinite(station.precipitacaoMm) ? station.precipitacaoMm : 0),
    0
  );
  const withRainCount = stations.reduce(
    (acc, station) => (station.precipitacaoMm > 0 ? acc + 1 : acc),
    0
  );

  return {
    mediaFazendaMm: total > 0 ? sum / total : 0,
    mediaChuvaMm: withRainCount > 0 ? sum / withRainCount : 0,
  };
}

export function formatFooterMm(value: number): string {
  if (!Number.isFinite(value)) {
    return `0,0 ${RAINFALL_UNIT}`;
  }
  return `${value.toFixed(1).replace(".", ",")} ${RAINFALL_UNIT}`;
}

function drawHeaderTitle(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  fazendaNome?: string | null
): void {
  const title = formatRainfallMapHeaderTitle(fazendaNome);

  ctx.save();
  ctx.font = "700 20px Arial, sans-serif";
  ctx.fillStyle = "#111";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(title, canvasWidth / 2, HEADER_CENTER_Y);
  ctx.restore();
}

function drawVerticalLegend(
  ctx: CanvasRenderingContext2D,
  bands: RainfallBand[],
  originX: number,
  originY: number
): void {
  if (!bands.length) {
    return;
  }

  const swatchSize = 14;
  const rowHeight = 22;
  const labelGap = 8;

  ctx.save();
  ctx.font = "600 14px Arial, sans-serif";
  ctx.fillStyle = "#555";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(`Índices (${RAINFALL_UNIT})`, originX, originY);

  bands.forEach((band, index) => {
    const y = originY + 14 + index * rowHeight;
    const color = band.color || "#ccc";

    ctx.fillStyle = color;
    ctx.fillRect(originX, y, swatchSize, swatchSize);
    ctx.strokeStyle = "#9aa0a6";
    ctx.lineWidth = 1;
    ctx.strokeRect(originX, y, swatchSize, swatchSize);

    ctx.fillStyle = "#555";
    ctx.font = "12px Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const label = (band.label || bandTickLabel(band)).replace(/\s*mm\s*/gi, "").trim();
    ctx.fillText(label, originX + swatchSize + labelGap, y + swatchSize / 2);
  });

  ctx.restore();
}

function drawNorthArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(6, 9);
  ctx.lineTo(0, 4);
  ctx.lineTo(-6, 9);
  ctx.closePath();
  ctx.fillStyle = "#111";
  ctx.fill();
  ctx.font = "700 11px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillText("N", 0, -16);
  ctx.restore();
}

function drawAmaggiLogo(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  logo: HTMLImageElement | null,
  maxWidth: number
): void {
  if (logo) {
    const naturalW = logo.naturalWidth || logo.width;
    const naturalH = logo.naturalHeight || logo.height;
    if (naturalW > 0 && naturalH > 0) {
      const scale = Math.min(
        maxWidth / naturalW,
        FOOTER_LOGO_MAX_HEIGHT / naturalH
      );
      const width = naturalW * scale;
      const height = naturalH * scale;
      ctx.drawImage(
        logo,
        centerX - width / 2,
        centerY - height / 2,
        width,
        height
      );
      return;
    }
  }

  ctx.save();
  ctx.font = "700 18px Georgia, 'Times New Roman', serif";
  ctx.fillStyle = "#0b3d91";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("AMAGGI", centerX, centerY);
  ctx.restore();
}

function drawFooter(
  ctx: CanvasRenderingContext2D,
  layout: MapLayout,
  periodo: RainfallExportPeriod | null | undefined,
  averages: RainfallExportAverages,
  logo: HTMLImageElement | null
): void {
  const y = layout.canvasHeight - FOOTER_HEIGHT;
  const width = layout.canvasWidth;
  const padX = 16;
  const columns = [
    { flex: 2.4, label: "Período", value: formatRainfallExportPeriod(periodo) },
    {
      flex: 1,
      label: "Média da Fazenda",
      value: formatFooterMm(averages.mediaFazendaMm),
    },
    {
      flex: 1,
      label: "Média da Chuva",
      value: formatFooterMm(averages.mediaChuvaMm),
    },
    { flex: 1.1, label: "", value: "" },
  ];
  const totalFlex = columns.reduce((acc, column) => acc + column.flex, 0);

  ctx.save();
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, y, width, FOOTER_HEIGHT);
  ctx.strokeStyle = "#111";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(width, y);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, y + 0.5, width - 1, FOOTER_HEIGHT - 1);

  let cursorX = 0;
  columns.forEach((column, index) => {
    const colWidth = (column.flex / totalFlex) * width;
    const nextX = cursorX + colWidth;

    if (index > 0) {
      ctx.beginPath();
      ctx.moveTo(cursorX, y);
      ctx.lineTo(cursorX, layout.canvasHeight);
      ctx.stroke();
    }

    if (index === columns.length - 1) {
      drawAmaggiLogo(
        ctx,
        cursorX + colWidth / 2,
        y + FOOTER_HEIGHT / 2,
        logo,
        Math.min(FOOTER_LOGO_MAX_WIDTH, colWidth - 24)
      );
    } else {
      ctx.fillStyle = "#111";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = "12px Arial, sans-serif";
      ctx.fillText(column.label, cursorX + padX, y + 12);
      ctx.font = "700 16px Arial, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText(column.value, cursorX + padX, y + FOOTER_HEIGHT / 2 + 10);
    }

    cursorX = nextX;
  });

  ctx.restore();
}

function drawScaleBar(
  ctx: CanvasRenderingContext2D,
  layout: MapLayout,
  x: number,
  y: number
): void {
  const midLat = (layout.bounds.north + layout.bounds.south) / 2;
  const metersPerDegreeLng =
    111320 * Math.cos((midLat * Math.PI) / 180);
  const mapWidthMeters =
    Math.abs(layout.bounds.east - layout.bounds.west) * metersPerDegreeLng;
  const metersPerPixel = mapWidthMeters / layout.mapWidth;

  const targetMeters = 4000;
  const barPx = Math.max(40, Math.min(180, targetMeters / metersPerPixel));
  const barMeters = barPx * metersPerPixel;
  const labelKm = (barMeters / 1000).toFixed(barMeters >= 1000 ? 0 : 1);

  ctx.save();
  ctx.strokeStyle = "#111";
  ctx.fillStyle = "#111";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + barPx, y);
  ctx.moveTo(x, y - 5);
  ctx.lineTo(x, y + 5);
  ctx.moveTo(x + barPx, y - 5);
  ctx.lineTo(x + barPx, y + 5);
  ctx.stroke();
  ctx.font = "11px Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("0", x - 2, y + 16);
  ctx.textAlign = "right";
  ctx.fillText(`${labelKm} km`, x + barPx + 2, y + 16);
  ctx.restore();
}

function drawTalhaoOutlines(
  ctx: CanvasRenderingContext2D,
  polygons: ReturnType<typeof extractTalhaoClipPolygons>,
  layout: MapLayout
): void {
  ctx.save();
  ctx.strokeStyle = "#111";
  ctx.lineWidth = 0.8;
  ctx.lineJoin = "round";

  polygons.forEach((rings) => {
    rings.forEach((ring, ringIndex) => {
      if (!ring.length) {
        return;
      }
      ctx.beginPath();
      ring.forEach((pos, i) => {
        const lng = pos[0] as number;
        const lat = pos[1] as number;
        const { x, y } = project(lat, lng, layout);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();
      // Outer ring stroke only (holes still stroked lightly).
      ctx.strokeStyle = ringIndex === 0 ? "#111" : "#666";
      ctx.stroke();
    });
  });

  ctx.restore();
}

function drawTalhaoCodes(
  ctx: CanvasRenderingContext2D,
  collection: FeatureCollection,
  layout: MapLayout
): void {
  ctx.save();
  ctx.font = "700 12px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#fff";
  ctx.fillStyle = "#111";

  collection.features.forEach((feature: Feature) => {
    const geometry = feature?.geometry;
    if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
      return;
    }

    const props = feature.properties as
      | {
          codigo?: string | number;
          pontoCentralLat?: number;
          pontoCentralLong?: number;
        }
      | null
      | undefined;

    const codigo = props?.codigo;
    if (codigo == null || codigo === "") {
      return;
    }

    let lat = props?.pontoCentralLat;
    let lng = props?.pontoCentralLong;

    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      // Fallback: first ring centroid approx (average of vertices).
      const ring =
        geometry.type === "Polygon"
          ? (geometry as Polygon).coordinates[0]
          : (geometry as MultiPolygon).coordinates[0]?.[0];
      if (!ring?.length) {
        return;
      }
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;
      ring.forEach((pos) => {
        const ringLng = pos[0];
        const ringLat = pos[1];
        if (isFiniteNumber(ringLat) && isFiniteNumber(ringLng)) {
          sumLat += ringLat;
          sumLng += ringLng;
          count += 1;
        }
      });
      if (!count) {
        return;
      }
      lat = sumLat / count;
      lng = sumLng / count;
    }

    const { x, y } = project(lat, lng, layout);
    if (
      x < layout.mapX ||
      x > layout.mapX + layout.mapWidth ||
      y < layout.mapY ||
      y > layout.mapY + layout.mapHeight
    ) {
      return;
    }

    const label = String(codigo);
    ctx.strokeText(label, x, y);
    ctx.fillText(label, x, y);
  });

  ctx.restore();
}

function drawStations(
  ctx: CanvasRenderingContext2D,
  stations: RainfallStation[],
  layout: MapLayout
): void {
  ctx.save();
  ctx.font = "800 11px Arial, sans-serif";
  ctx.textBaseline = "middle";

  stations.forEach((station) => {
    if (!(station.precipitacaoMm > 0)) {
      return;
    }

    const { x, y } = project(station.lat, station.lng, layout);
    const label = formatStationLabel(station.precipitacaoMm);

    ctx.beginPath();
    ctx.arc(x, y, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = "#1b5e20";
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#fff";
    ctx.stroke();

    ctx.lineWidth = 3;
    ctx.strokeStyle = "#fff";
    ctx.fillStyle = "#e60000";
    ctx.strokeText(label, x + 8, y);
    ctx.fillText(label, x + 8, y);
  });

  ctx.restore();
}

function buildLayout(
  bounds: GeoBounds,
  width: number,
  legendItemCount = 0
): MapLayout {
  const geoWidth = Math.max(bounds.east - bounds.west, 1e-9);
  const geoHeight = Math.max(bounds.north - bounds.south, 1e-9);
  const mapWidth = width - PAD_LEFT - PAD_RIGHT;
  const mapHeight = Math.round(mapWidth * (geoHeight / geoWidth));
  const legendBottom =
    HEADER_CENTER_Y + 14 + Math.max(legendItemCount, 0) * 22 + 16;
  const canvasHeight = Math.max(
    mapHeight + PAD_TOP + PAD_BOTTOM,
    legendBottom + PAD_BOTTOM
  );

  return {
    canvasWidth: width,
    canvasHeight,
    mapX: PAD_LEFT,
    mapY: PAD_TOP,
    mapWidth,
    mapHeight,
    bounds,
  };
}

/**
 * Builds a white-background PNG of the rainfall map with a continuous
 * legend bar (top-left), talhão outlines/codes, and station mm labels.
 */
export async function buildRainfallMapPngCanvas(
  options: ExportRainfallMapPngOptions
): Promise<HTMLCanvasElement> {
  const {
    points,
    indices,
    selectedBandCodes,
    fazendaNome,
    periodo,
    width = 1600,
  } = options;

  const bands = resolveRainfallBands(indices);
  const stations = dedupeRainfallStations(
    extractRainfallSamples(points, bands)
  );
  const clipPolygons = extractTalhaoClipPolygons(points);
  const averages = computeRainfallExportAverages(stations);

  if (!stations.length) {
    throw new Error("Não há dados de precipitação para exportar neste período.");
  }

  if (!selectedBandCodes.length) {
    throw new Error("Selecione ao menos uma faixa da legenda para exportar.");
  }

  const baseBounds = resolveOverlayBounds(stations, points.Properties);
  if (!baseBounds) {
    throw new Error("Não foi possível calcular a área do mapa para exportação.");
  }

  const bounds = expandBoundsWithClipPolygons(baseBounds, clipPolygons);
  const layout = buildLayout(bounds, width, bands.length);

  const surface = await renderRainfallSurfaceAsync({
    stations,
    bands,
    selectedBandCodes,
    bounds,
    clipPolygons,
    width: Math.max(DEFAULT_SURFACE_WIDTH, Math.round(layout.mapWidth)),
  });

  if (!surface) {
    throw new Error("Falha ao renderizar a superfície de chuva.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = layout.canvasWidth;
  canvas.height = layout.canvasHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D indisponível para exportação.");
  }

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const surfaceImage = await loadImage(surface.url);
  const logoImage = await loadImageOptional(AMAGGI_LOGO_URL);
  ctx.drawImage(
    surfaceImage,
    layout.mapX,
    layout.mapY,
    layout.mapWidth,
    layout.mapHeight
  );

  drawTalhaoOutlines(ctx, clipPolygons, layout);
  drawTalhaoCodes(ctx, points, layout);
  drawStations(ctx, stations, layout);

  drawHeaderTitle(ctx, layout.canvasWidth, fazendaNome);
  drawNorthArrow(ctx, layout.canvasWidth - 36, HEADER_CENTER_Y);
  drawVerticalLegend(ctx, bands, 16, HEADER_CENTER_Y);
  drawScaleBar(
    ctx,
    layout,
    layout.mapX,
    layout.canvasHeight - FOOTER_HEIGHT - 22
  );
  drawFooter(ctx, layout, periodo, averages, logoImage);

  return canvas;
}

export async function buildRainfallMapPngBlob(
  options: ExportRainfallMapPngOptions
): Promise<Blob> {
  const canvas = await buildRainfallMapPngCanvas(options);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Falha ao gerar o arquivo PNG."));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export async function exportRainfallMapPng(
  options: ExportRainfallMapPngOptions
): Promise<void> {
  const blob = await buildRainfallMapPngBlob(options);
  const filename =
    options.filename ??
    `mapa-de-chuva-${new Date().toISOString().slice(0, 10)}.png`;
  downloadBlob(blob, filename);
}
